import React, { useState, useContext, useEffect, useRef } from 'react';
import { Table } from 'reactstrap';
import { AppContext } from '../application/provider';
import CapacitacionesService from '../axios_services/capacitaciones.service';
import Loader from '../components/Loader/Loader';
import { ButtonSearch, ButtonInsert, ButtonUpdate, ButtonDelete } from '../components/Buttons/Buttons';
import API_URL_BASE from '../axios_services/apiConfig';
import { FileMinus } from 'lucide-react';
import ModalCapacitacion from '../components/Modal/ModalCapacitacion';
import ModalConfirmar from '../components/Modal/ModalConfirmar';
import './Capacitaciones.css';

const ESTADOS_LABEL = {
    1: { label: 'Programada', color: '#3b82f6' },
    2: { label: 'En Proceso', color: '#f59e0b' },
    3: { label: 'Realizada', color: '#3cb167' },
};

function Capacitaciones() {
    const [state] = useContext(AppContext);
    const [registros, setRegistros] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedRegistro, setSelectedRegistro] = useState(null);
    const [confirmModal, setConfirmModal] = useState({ isOpen: false, registro: null });
    const [uploadTarget, setUploadTarget] = useState(null);
    const [uploadType, setUploadType] = useState(null); // 'asistencia' o 'evidencia'
    const uploadFileRef = useRef(null);
    const initialFetchDone = useRef(false);

    const [filtroFecha, setFiltroFecha] = useState(() => {
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    });
    const [filtroModalidad, setFiltroModalidad] = useState('');

    const personal = state.user?.personal || [];
    const perfilId = state.user?.perfil_id;
    const usuarioId = state.user?.usuario_id;

    useEffect(() => {
        if (!initialFetchDone.current) {
            loadData();
            initialFetchDone.current = true;
        }
    }, []);

    const loadData = async () => {
        setIsLoading(true);
        try {
            const params = { perfil_id: perfilId };
            if (filtroFecha) params.periodo = filtroFecha;
            if (filtroModalidad) params.modalidad = filtroModalidad;
            const response = await CapacitacionesService.getCapacitaciones(params);
            setRegistros(response.data || []);
        } catch (error) {
            console.error(error);
            setRegistros([]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleNuevo = () => {
        setSelectedRegistro(null);
        setIsModalOpen(true);
    };

    const handleEditar = (registro) => {
        setSelectedRegistro(registro);
        setIsModalOpen(true);
    };

    const handleCloseModal = (shouldReload = false) => {
        setIsModalOpen(false);
        setSelectedRegistro(null);
        if (shouldReload === true) loadData();
    };

    const handleSave = async (data, archivoAsistencia, archivoEvidencia) => {
        try {
            if (data.id_capacitacion) {
                await CapacitacionesService.updateCapacitacion(data.id_capacitacion, data, archivoAsistencia, archivoEvidencia);
            } else {
                await CapacitacionesService.insertCapacitacion(data, archivoAsistencia, archivoEvidencia);
            }
            return true;
        } catch (error) {
            alert(error.response?.data?.message || 'Ocurrió un error al guardar.');
            throw error;
        }
    };

    const handleEliminar = (registro) => {
        setConfirmModal({ isOpen: true, registro });
    };

    const confirmarEliminar = async () => {
        const registro = confirmModal.registro;
        setConfirmModal({ isOpen: false, registro: null });
        setIsLoading(true);
        try {
            await CapacitacionesService.deleteCapacitacion(registro.id_capacitacion, perfilId);
            await loadData();
        } catch (error) {
            setIsLoading(false);
            alert(error.response?.data?.message || 'Ocurrió un error al eliminar.');
        }
    };

    const handleVerAsistencia = (registro) => {
        const base = process.env.REACT_APP_STORAGE_URL || API_URL_BASE.replace('api/', 'storage/');
        window.open(`${base}${registro.archivo_asistencia_url}`, '_blank');
    };

    const handleVerEvidencia = (registro) => {
        const base = process.env.REACT_APP_STORAGE_URL || API_URL_BASE.replace('api/', 'storage/');
        window.open(`${base}${registro.archivo_evidencia_url}`, '_blank');
    };

    const handleSubirDesdeIcono = (registro, type) => {
        setUploadTarget(registro);
        setUploadType(type);
        if (uploadFileRef.current) {
            uploadFileRef.current.value = '';
            uploadFileRef.current.click();
        }
    };

    const handleUploadFileSelected = async (e) => {
        const archivo = e.target.files[0];
        if (!archivo || !uploadTarget || !uploadType) return;
        setIsLoading(true);
        try {
            if (uploadType === 'asistencia') {
                await CapacitacionesService.subirAsistencia(uploadTarget.id_capacitacion, archivo, perfilId, usuarioId);
            } else if (uploadType === 'evidencia') {
                await CapacitacionesService.subirEvidencia(uploadTarget.id_capacitacion, archivo, perfilId, usuarioId);
            }
            await loadData();
        } catch (error) {
            setIsLoading(false);
            alert(error.response?.data?.message || 'Error al subir el documento.');
        }
        setUploadTarget(null);
        setUploadType(null);
    };

    const formatFecha = (fecha) => {
        if (!fecha) return '-';
        return fecha.split('T')[0];
    };

    const getNombreInstructor = (id) => {
        const u = personal.find(p => p.usuario_id == id);
        return u ? u.nombre_completo_usuario : `ID: ${id}`;
    };

    if (isLoading) return <Loader />;

    return (
        <div className="caja-flujo-container">
            <h1>REGISTRO DE CAPACITACIONES</h1>

            <div className="filtros">
                <div className="campo-fecha">
                    <label>Periodo:</label>
                    <input
                        type="month"
                        value={filtroFecha}
                        onChange={(e) => setFiltroFecha(e.target.value)}
                    />
                </div>
                <div className="campo-fecha">
                    <label>Modalidad:</label>
                    <select
                        value={filtroModalidad}
                        onChange={(e) => setFiltroModalidad(e.target.value)}
                        className="form-control"
                    >
                        <option value="">Todas</option>
                        <option value="Presencial">Presencial</option>
                        <option value="Virtual">Virtual</option>
                        <option value="Híbrida">Híbrida</option>
                    </select>
                </div>
            </div>

            <ButtonSearch onClick={loadData} isLoading={isLoading} />

            <div className="nuevo-movimiento-section">
                {[5, 8, 22, 26].includes(Number(perfilId)) && (
                    <ButtonInsert onClick={handleNuevo} />
                )}
            </div>

            <div className="table-container">
                <Table>
                    <thead>
                        <tr>
                            <th>N°</th>
                            <th>Título</th>
                            <th>Fecha</th>
                            <th>Duración</th>
                            <th>Modalidad</th>
                            <th>Cupo</th>
                            <th>Instructor</th>
                            <th>Estado</th>
                            <th style={{ textAlign: 'center' }}>Asistencia</th>
                            <th style={{ textAlign: 'center' }}>Evidencia</th>
                            <th>Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {registros.length > 0 ? (
                            registros.map((reg, index) => {
                                const estadoInfo = ESTADOS_LABEL[reg.estado];
                                return (
                                    <tr key={reg.id_capacitacion}>
                                        <td>{index + 1}</td>
                                        <td>{reg.titulo}</td>
                                        <td>{formatFecha(reg.fecha)}</td>
                                        <td>{reg.duracion} min</td>
                                        <td>{reg.modalidad}</td>
                                        <td>{reg.cupo_maximo}</td>
                                        <td>{reg.nombre_instructor || getNombreInstructor(reg.id_instructor)}</td>
                                        <td>
                                            <span style={{ color: estadoInfo?.color || '#666', fontWeight: 'bold' }}>
                                                {estadoInfo?.label || reg.estado}
                                            </span>
                                        </td>
                                        <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                                            {(() => {
                                                const tieneDoc = Number(reg.tiene_asistencia) === 1;
                                                return (
                                                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                                                        <span
                                                            style={{ cursor: 'pointer' }}
                                                            onClick={() => tieneDoc ? handleVerAsistencia(reg) : handleSubirDesdeIcono(reg, 'asistencia')}
                                                            title={tieneDoc ? 'Ver asistencia' : 'Subir documento de asistencia'}
                                                        >
                                                            <FileMinus size={20} style={{ color: tieneDoc ? '#3cb167' : '#f87171', opacity: tieneDoc ? 1 : 0.7 }} />
                                                        </span>
                                                    </div>
                                                );
                                            })()}
                                        </td>
                                        <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                                            {(() => {
                                                const tieneEvidencia = Number(reg.tiene_evidencia) === 1;
                                                return (
                                                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                                                        <span
                                                            style={{ cursor: 'pointer' }}
                                                            onClick={() => tieneEvidencia ? handleVerEvidencia(reg) : handleSubirDesdeIcono(reg, 'evidencia')}
                                                            title={tieneEvidencia ? 'Ver evidencia' : 'Subir documento de evidencia'}
                                                        >
                                                            <FileMinus size={20} style={{ color: tieneEvidencia ? '#3cb167' : '#f87171', opacity: tieneEvidencia ? 1 : 0.7 }} />
                                                        </span>
                                                    </div>
                                                );
                                            })()}
                                        </td>
                                        <td>
                                            {[5, 8, 22, 26].includes(Number(perfilId)) && (
                                                <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                                                    <ButtonUpdate onClick={() => handleEditar(reg)} />
                                                    <ButtonDelete onClick={() => handleEliminar(reg)} />
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })
                        ) : (
                            <tr>
                                <td colSpan={11} className="text-center text-gray-500 py-4">
                                    No hay capacitaciones registradas.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </Table>
            </div>

            <ModalCapacitacion
                isOpen={isModalOpen}
                registro={selectedRegistro}
                onSave={handleSave}
                onClose={handleCloseModal}
                usuarioId={usuarioId}
                perfilId={perfilId}
                personal={personal}
            />

            <ModalConfirmar
                isOpen={confirmModal.isOpen}
                titulo="Eliminar capacitación"
                mensaje={`¿Está seguro de eliminar la capacitación "${confirmModal.registro?.titulo}"?`}
                onConfirmar={confirmarEliminar}
                onCancelar={() => setConfirmModal({ isOpen: false, registro: null })}
                textConfirmar="Eliminar"
                textCancelar="Cancelar"
                variante="peligro"
            />

            <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                ref={uploadFileRef}
                style={{ display: 'none' }}
                onChange={handleUploadFileSelected}
            />
        </div>
    );
}

export default Capacitaciones;
